import type { UserProfile } from "@elaso/shared/schemas/profile";
import { isViewMounted } from "../active-assets";
import {
	messageContainsMention,
	playNotificationSound,
	shouldPlayNotification,
} from "../notification-sound";
import { authStore } from "./auth-store";
import type { ChannelEntry, CoreStoreState, UserEntry } from "./core-store";

// Generic UPDATE_STATE handler.
//
// Resolves any key (flat or dot-notation) and any mode (set/append/remove)
// by inspecting the current store value type.
//
// Nested keys like "channels.readStatus" are split into [parentKey, field].
//   • Parent Map, array value: merge each item by its id into the parent Map
//     entries (sub-field update via `entryField` config).
//   • Parent Map, object value: treat keys as Map entry ids and merge each
//     value into that entry's sub-field.
//
// A small config handles the few cases that cannot be inferred:
//   • Custom id resolvers for array items (e.g. firebaseUId vs firebaseUid)
//   • Value transformers (e.g. UserRole[] → .roles per entry)
//   • "set" mode hooks that clear a field before merging (e.g. presences)

type NestedKeyConfig = {
	// For array values, resolve the Map key from each item. Defaults to item.id.
	getItemId?: (item: any) => string;
	// For array values, extract the value to merge from each item. Defaults to
	// the item itself.
	getEntryValue?: (item: any) => any;
	// When mode=set, clear this field on all existing entries first
	clearOnSet?: boolean;
	// Write to a different top-level store key instead of the parent Map.
	redirectTo?: string;
	createDefault?: (id: string) => any;
	// Field name to use for remove operations, when it differs from the key suffix.
	removeField?: string;
	// Merge into entries that already exist and skip the rest, instead of
	// manufacturing one. For nested keys whose payload carries no identity of its
	// own. Without this, a push for an entry the client does not hold creates a
	// bodiless record, no id and no name, that still carries the pushed field.
	existingOnly?: boolean;
	// Given the entry's current field value and the incoming one, return what
	// to store. Lets a key refuse regressions instead of last-write-wins.
	resolveFieldValue?: (existing: unknown, incoming: unknown) => unknown;
};

const defaultUserEntry = (id: string): UserEntry => ({
	profile: { firebaseUid: id, email: "", displayName: id } as UserProfile,
	roles: [],
});

const nestedKeyConfig: Record<string, NestedKeyConfig> = {
	"users.profiles": {
		getItemId: (item) => item.firebaseUid,
		getEntryValue: (item) => ({ profile: item }),
		createDefault: defaultUserEntry,
	},
	"users.roles": {
		getItemId: (item) => item.firebaseUid,
		getEntryValue: (item) => ({ roles: item.roles }),
		createDefault: defaultUserEntry,
	},
	"users.presences": {
		getItemId: (item) => item.firebaseUid,
		getEntryValue: (item) => {
			const { profile, ...presence } = item;
			const result: Partial<UserEntry> = { presence };
			if (profile) result.profile = profile;
			return result;
		},
		clearOnSet: true,
		createDefault: defaultUserEntry,
		removeField: "presence",
	},
	// An ACL push carries no channel identity, only `{ [channelId]: permissions }`,
	// so merging it into a channel the client does not hold produced an entry
	// with no id, no name and no `selfPermission`, yet holding that channel's full
	// role list. Nothing rendered it (the sidebar filters on access), which is why
	// it went unnoticed, but it is exactly the structure disclosure the audience
	// model exists to prevent. Registered so an unhandled push can never do that
	// again, even if a server audience regresses (TECH-3152).
	"channels.permissions": {
		existingOnly: true,
	},
	// Same shape, same reason. A `roleKeys` push is `{ [channelId]: keys }` with no
	// channel identity of its own. This is the field `/app` receives where the ops
	// path receives `permissions` above
	// (docs/adr/24-what-the-channel-list-sends-to-the-client.md), so it needs the
	// same guard against manufacturing a bodiless entry that still carries a
	// channel's role list.
	"channels.roleKeys": {
		existingOnly: true,
	},
	// Same shape, same reason, different key. A reaction push carries only
	// `{ [messageId]: reactions }` and no message identity. Reactions reach everyone
	// entitled to the channel, including users who never opened it and hold no
	// message entry, so without this those clients accumulate bodiless message
	// records. Skipping them loses nothing, because the reactions arrive with the
	// message itself when history is first fetched (TECH-3158).
	"messages.reactions": {
		existingOnly: true,
	},
	// The client advances its read position optimistically (TECH-3283), so the
	// markRead echo, or a cross-connection echo carrying another device's older
	// position, can arrive BEHIND the held value. Regressing to it would flash
	// the unread marker the optimistic advance just suppressed. Message ids
	// order lexicographically, so "newer" is a plain string compare; anything
	// non-string falls through to last-write-wins.
	"channels.readStatus": {
		resolveFieldValue: (existing, incoming) =>
			typeof existing === "string" &&
			typeof incoming === "string" &&
			existing > incoming
				? existing
				: incoming,
	},
	workspacesElsewhere: {
		getItemId: (row) => row.connectionId,
	},
};

function getOrCreate<T>(
	map: Map<string, T>,
	entryId: string,
	createDefault?: (entryId: string) => T
): T {
	return (
		map.get(entryId) ?? (createDefault ? createDefault(entryId) : ({} as T))
	);
}

function mergeArrayIntoMap<T>(
	map: Map<string, T>,
	items: any[],
	config: NestedKeyConfig
): void {
	const getId = config.getItemId ?? ((item: any) => item.id);
	const getValue = config.getEntryValue;
	for (const item of items) {
		const itemId = getId(item);
		if (!itemId) continue;
		const existing = getOrCreate(map, itemId, config.createDefault);
		map.set(itemId, { ...existing, ...(getValue ? getValue(item) : item) });
	}
}

function mergeRecordIntoMap<T>(
	map: Map<string, T>,
	record: Record<string, unknown>,
	field: string,
	config: Pick<
		NestedKeyConfig,
		"createDefault" | "existingOnly" | "resolveFieldValue"
	> = {}
): void {
	for (const [id, fieldValue] of Object.entries(record)) {
		if (config.existingOnly && !map.has(id)) continue;
		const existing = getOrCreate(map, id, config.createDefault);
		const resolved = config.resolveFieldValue
			? config.resolveFieldValue(
					(existing as Record<string, unknown>)[field],
					fieldValue
				)
			: fieldValue;
		map.set(id, { ...existing, [field]: resolved });
	}
}

// A composite value looks like { items: [...], readStatus: {...}, roles: {...} }.
// Build the Map from items, then merge each remaining key as a sub-field on the
// matching entries.
function applyCompositeSet<T extends { id?: string }>(
	currentMap: Map<string, T>,
	composite: { items: any[]; [field: string]: unknown }
): Map<string, T> {
	const { items, ...fields } = composite;
	const result = new Map<string, T>();
	for (const item of items) {
		result.set(item.id, { ...currentMap.get(item.id), ...item });
	}
	for (const [field, record] of Object.entries(fields)) {
		if (record && typeof record === "object" && !Array.isArray(record)) {
			// A composite "set" is an authoritative full snapshot. Each sibling
			// field record (readStatus, muteStatus, …) is the COMPLETE set of values
			// for that field. Clear any stale carry-over from the previous entry
			// before applying the record, so a channel absent from the record drops
			// the field instead of retaining a dead value. Without this, a once-set
			// `muteStatus: null` (indefinite mute) survived every channel.list and
			// muted the channel forever, suppressing unread markers and
			// notification sounds across reloads (TECH-3003).
			for (const [entryId, entry] of result) {
				if ((entry as Record<string, unknown>)[field] !== undefined) {
					result.set(entryId, { ...entry, [field]: undefined });
				}
			}
			for (const [entryId, fieldValue] of Object.entries(
				record as Record<string, unknown>
			)) {
				const existing = result.get(entryId);
				if (existing) result.set(entryId, { ...existing, [field]: fieldValue });
			}
		}
	}
	return result;
}

function isCompositeValue(
	value: unknown
): value is { items: any[]; [field: string]: unknown } {
	return (
		value !== null &&
		typeof value === "object" &&
		!Array.isArray(value) &&
		Array.isArray((value as any).items)
	);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object" && !Array.isArray(value);
}

function applyMapUpdate<T extends { id?: string }>(
	currentMap: Map<string, T>,
	mode: string | undefined,
	value: unknown
): Map<string, T> {
	const map = new Map(currentMap);
	if (mode === "set" && isCompositeValue(value)) {
		return applyCompositeSet(map, value);
	}
	if (mode === "set" && Array.isArray(value)) {
		const result = new Map<string, T>();
		for (const item of value) {
			result.set(item.id, { ...map.get(item.id), ...item });
		}
		return result;
	}
	if (mode === "set" && isPlainObject(value)) {
		for (const [key, val] of Object.entries(value as Record<string, T>)) {
			map.set(key, val);
		}
		return map;
	}
	if (mode === "append" && Array.isArray(value)) {
		for (const item of value) {
			if (item.id) map.set(item.id, { ...map.get(item.id), ...item });
		}
		return map;
	}
	if (mode === "remove" && (value as { id?: string })?.id) {
		map.delete((value as { id: string }).id);
		return map;
	}
	return map;
}

type NestedUpdateOpts = {
	parentMap: Map<string, any>;
	field: string;
	mode: string | undefined;
	value: unknown;
	config: NestedKeyConfig;
};

// A nested key update, e.g. "channels.readStatus" or "users.presences".
function applyNestedUpdate(opts: NestedUpdateOpts): Map<string, any> {
	const { field, mode, value, config } = opts;
	const map = new Map(opts.parentMap);

	if (Array.isArray(value)) {
		if (mode === "set" && config.clearOnSet) {
			for (const [entryId, entry] of map) {
				if (entry[field] !== undefined) {
					map.set(entryId, { ...entry, [field]: undefined });
				}
			}
		}
		mergeArrayIntoMap(map, value, config);
		return map;
	}

	if (mode === "remove" && (value as { id?: string })?.id) {
		const removeId = (value as { id: string }).id;
		const existing = map.get(removeId);
		const fieldToRemove = config.removeField ?? field;
		if (existing)
			map.set(removeId, { ...existing, [fieldToRemove]: undefined });
		return map;
	}

	if (value && typeof value === "object") {
		mergeRecordIntoMap(map, value as Record<string, unknown>, field, config);
		return map;
	}

	return map;
}

type SetStateFn = (storeKey: string, storeValue: unknown) => void;

function updateChannelLatestMessages(
	channelsMap: Map<string, ChannelEntry>,
	newMessages: any[],
	state: CoreStoreState
): Map<string, ChannelEntry> {
	const channels = new Map(channelsMap);
	let playedNotification: "none" | "regular" | "mention" = "none";
	for (const msg of newMessages) {
		if (msg.channelId && msg.id) {
			const channel = channels.get(msg.channelId);
			if (
				channel &&
				(!channel.latestMessageId || msg.id > channel.latestMessageId)
			) {
				const next: ChannelEntry = {
					...channel,
					latestMessageId: msg.id,
					latestMessageAt: msg.createdAt,
				};
				// A message landing in a channel mounted as a full view is read
				// the moment it is recorded. Advancing the read position in the
				// SAME update keeps the unread marker from flashing during the
				// markRead round-trip (TECH-3283). forcedUnreadMessageId is
				// deliberately left alone, because clearing it is markRead's job,
				// here as on the server.
				if (
					isViewMounted({ type: "channel", id: msg.channelId }) &&
					(!next.readStatus || msg.id > next.readStatus)
				) {
					next.readStatus = msg.id;
				}
				channels.set(msg.channelId, next);

				const currentUserId = authStore.getState().user?.uid;

				if (
					shouldPlayNotification(msg.channelId, msg.authorId, currentUserId)
				) {
					const userRoles = currentUserId
						? (state.users.get(currentUserId)?.roles ?? [])
						: [];
					const isMention =
						!!currentUserId &&
						messageContainsMention(msg.richContent, currentUserId, userRoles);

					if (
						playedNotification === "none" ||
						(playedNotification === "regular" && isMention)
					) {
						playNotificationSound(undefined, isMention);
						playedNotification = isMention ? "mention" : "regular";
					}
				}
			}
		}
	}
	return channels;
}

type FlatUpdateOpts = {
	state: CoreStoreState;
	setState: SetStateFn;
	storeKey: string;
	mode: string | undefined;
	value: unknown;
	config: NestedKeyConfig;
};

function applyFlatUpdate(opts: FlatUpdateOpts): void {
	const { state, setState, storeKey, mode, value, config } = opts;
	const currentValue = state[storeKey as keyof CoreStoreState];

	if (mode === "remove" && Array.isArray(currentValue) && config.getItemId) {
		const getId = config.getItemId;
		const removeId = getId(value);
		setState(
			storeKey,
			currentValue.filter((item) => getId(item) !== removeId)
		);
		return;
	}

	if (!(currentValue instanceof Map)) {
		setState(storeKey, value);
		return;
	}

	setState(
		storeKey,
		applyMapUpdate(currentValue as Map<string, any>, mode, value)
	);

	if (storeKey === "messages" && mode === "append" && Array.isArray(value)) {
		setState(
			"channels",
			updateChannelLatestMessages(state.channels, value, state)
		);
	}
}

type TypingHandler = {
	addTypingUser: (channelId: string, firebaseUid: string) => void;
};

type SideEffectHandlers = {
	onBroadcastCleared?: () => void;
};

export function handleUpdateStateMessage(
	message: { key: string; mode?: string; value: unknown; pagination?: any },
	state: CoreStoreState & { updatePagination: (p: any) => void },
	setState: SetStateFn,
	typingHandler?: TypingHandler,
	sideEffects?: SideEffectHandlers
): void {
	const msgKey: string = message.key;
	const msgMode: string | undefined = message.mode;
	const msgValue: unknown = message.value;

	// Typing events. Side effects are the caller's job. Only `append` exists on
	// the wire, because the server never broadcasts typing removes; indicators
	// expire on the client TTL.
	if (msgKey === "typing" && typingHandler) {
		const { channelId, firebaseUid } = msgValue as {
			channelId: string;
			firebaseUid: string;
		};
		if (msgMode === "append") {
			typingHandler.addTypingUser(channelId, firebaseUid);
		}
		return;
	}

	const config = nestedKeyConfig[msgKey];
	if (config?.redirectTo) {
		setState(config.redirectTo, msgValue);
		return;
	}

	const dotIndex = msgKey.indexOf(".");
	const isNested = dotIndex !== -1;
	const parentKey = isNested ? msgKey.slice(0, dotIndex) : msgKey;
	const field = isNested ? msgKey.slice(dotIndex + 1) : null;

	if (isNested && field) {
		const parentMap = state[parentKey as keyof CoreStoreState];
		if (parentMap instanceof Map) {
			setState(
				parentKey,
				applyNestedUpdate({
					parentMap,
					field,
					mode: msgMode,
					value: msgValue,
					config: config ?? {},
				})
			);
		}
	} else {
		applyFlatUpdate({
			state,
			setState,
			storeKey: parentKey,
			mode: msgMode,
			value: msgValue,
			config: config ?? {},
		});

		if (
			parentKey === "broadcast" &&
			!msgValue &&
			sideEffects?.onBroadcastCleared
		) {
			sideEffects.onBroadcastCleared();
		}
	}

	if (message.pagination) {
		state.updatePagination({ ...message.pagination, isLoading: false });
	}
}
